import {
    CUSTOM_ELEMENTS_SCHEMA,
    ModuleWithProviders,
    NgModule,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslationModule } from '@ng-omar/translation';
import { MnmWebappModule } from 'mnm-webapp';
import { NgSelectModule } from '@ng-select/ng-select';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
    AchievementGaugeComponent,
    AlertComponent,
    ContentLoadingComponent,
    DeleteButtonComponent,
    DepartmentLinkComponent,
    DepartmentListFieldComponent,
    DropdownComponent,
    EditButtonComponent,
    AttachmentInputComponent,
    FileInputComponent,
    GaugeComponent,
    GoBackButtonComponent,
    HistoryListComponent,
    ImageComponent,
    ListLoadingComponent,
    LoadingComponent,
    LoadingRingComponent,
    MnmFormComponent,
    OperationLinkComponent,
    PermissionLinkComponent,
    PlanLinkComponent,
    ProgressBarComponent,
    ProgressBarInputComponent,
    ProgressRingComponent,
    RangeSliderComponent,
    SearchInputComponent,
    SidebarComponent,
    StrategicGoalLinkComponent,
    PlanDashboardComponent,
    TabComponent,
    TableOrderButtonComponent,
    TableOrderColumnComponent,
    TablePaginationComponent,
    TabListComponent,
    TeamLinkComponent,
    ToggleSwitchComponent,
    ResourceOptionalSettingRowComponent,
    UserLinkComponent,
    ValueLoadingComponent,
    YearDropdownComponent,
} from './components';
import {
    CardStaggerDirective,
    ClearSearchOnSelectDirective,
    ClickOutsideDirective,
    LegacyNgSelectDirective,
    FlatPickrDirective,
    HasAnyPermissionIdDirective,
    HasPermissionIdDirective,
    HighlightDirective,
    IsNotOriginDirective,
    IsOriginDirective,
    ParentsScrollListenerDirective,
    ScrollPositionDirective,
    TooltipDirective,
    WaitUntilLoadedDirective,
    WaitUntilLoadedLoadingComponent,
} from './directives';
import {
    CallFunctionPipe,
    ConcatArraysPipe,
    DaysLeftPipe,
    EnumeratePipe,
    FileSizePipe,
    HasPermissionIdPipe,
    MarkedPipe,
    MathAbsPipe,
    MeasurementCyclePipe,
    NestedPropertyPipe,
    PushToArrayPipe,
    RandomizePipe,
    RangeFromToPipe,
    ReverseArrayPipe,
    RoundPipe,
    SafeHtmlPipe,
    SafeUrlPipe,
    SearchPipe,
    TakePipe,
    ToDatePipe,
} from './pipes';
import { ColorPickerDirective } from 'ngx-color-picker';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { ExportPdfComponent } from './components/export-pdf/export-pdf.component';
import { FilterPipe } from '@masar/shared/pipes/filter.pipe';
import { ItemsAreUnavailableComponent } from '@masar/shared/components/items-are-unavailable/items-are-unavailable.component';
import { TreeSelectComponent } from './components/tree-select/tree-select.component';
import { NzTreeSelectModule } from 'ng-zorro-antd/tree-select';
import { ComingSoonComponent } from './components/coming-soon/coming-soon.component';
import { ToasterComponent } from './components/toaster/toaster.component';
import { ConfirmDialogModule } from '@masar/features/confirm-dialog';

const sharedComponents = [
    ImageComponent,
    MnmFormComponent,
    TablePaginationComponent,
    LoadingComponent,
    ListLoadingComponent,
    ProgressRingComponent,
    ContentLoadingComponent,
    ValueLoadingComponent,
    LoadingRingComponent,
    GaugeComponent,
    AchievementGaugeComponent,
    TabListComponent,
    TabComponent,
    PermissionLinkComponent,
    GoBackButtonComponent,
    ProgressBarComponent,
    DeleteButtonComponent,
    EditButtonComponent,
    AlertComponent,
    DropdownComponent,
    TableOrderButtonComponent,
    TableOrderColumnComponent,
    SidebarComponent,
    SearchInputComponent,
    ProgressBarInputComponent,
    RangeSliderComponent,
    OperationLinkComponent,
    UserLinkComponent,
    DepartmentLinkComponent,
    DepartmentListFieldComponent,
    StrategicGoalLinkComponent,
    PlanDashboardComponent,
    PlanLinkComponent,
    TreeSelectComponent,
    ComingSoonComponent,
    ExportPdfComponent,
    HistoryListComponent,
    ItemsAreUnavailableComponent,
    AttachmentInputComponent,
    FileInputComponent,
    TeamLinkComponent,
    ToggleSwitchComponent,
    ResourceOptionalSettingRowComponent,
    ToasterComponent,
    YearDropdownComponent,
];

const sharedDirectives = [
    FlatPickrDirective,
    HasPermissionIdDirective,
    HasAnyPermissionIdDirective,
    WaitUntilLoadedDirective,
    TooltipDirective,
    HighlightDirective,
    ClickOutsideDirective,
    ScrollPositionDirective,
    ParentsScrollListenerDirective,
    IsOriginDirective,
    IsNotOriginDirective,
    ClearSearchOnSelectDirective,
    CardStaggerDirective,
    LegacyNgSelectDirective,
];

const sharedPipes = [
    SafeUrlPipe,
    SafeHtmlPipe,
    EnumeratePipe,
    RandomizePipe,
    TakePipe,
    RoundPipe,
    DaysLeftPipe,
    RangeFromToPipe,
    FileSizePipe,
    ReverseArrayPipe,
    MathAbsPipe,
    ToDatePipe,
    HasPermissionIdPipe,
    NestedPropertyPipe,
    ConcatArraysPipe,
    CallFunctionPipe,
    SearchPipe,
    PushToArrayPipe,
    FilterPipe,
    MeasurementCyclePipe,
    MarkedPipe,
];

@NgModule({
    imports: [
        CommonModule,
        RouterModule,
        TranslationModule,
        RouterModule,
        NgSelectModule,
        ReactiveFormsModule,
        FormsModule,
        MnmWebappModule,
        ColorPickerDirective,
        NzProgressModule,
        NzTreeSelectModule,
        ConfirmDialogModule,
    ],
    declarations: [
        // components used by the directives
        WaitUntilLoadedLoadingComponent,

        // components
        sharedComponents,

        // directives
        sharedDirectives,

        // pipes
        sharedPipes,

        CallFunctionPipe,
    ],
    exports: [
        // modules
        NgSelectModule,
        FormsModule,
        ReactiveFormsModule,
        ConfirmDialogModule,

        // components
        sharedComponents,

        // directives
        sharedDirectives,

        // pipes
        sharedPipes,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SharedModule {
    public static forRoot(): ModuleWithProviders<SharedModule> {
        return { ngModule: SharedModule, providers: [] };
    }
}
